import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  writeBatch,
} from 'firebase/firestore';
import type { AdministratorLead, LeadSource } from '../types/administratorLeads';

const ADMINISTRATOR_LEADS_COLLECTION = 'administratorLeads';

export async function getAdministratorLeads(
  pageSize?: number,
  lastDoc?: QueryDocumentSnapshot
): Promise<{ administratorLeads: AdministratorLead[]; lastDoc?: QueryDocumentSnapshot }> {
  console.log('[API] getAdministratorLeads called:', { pageSize, hasLastDoc: !!lastDoc });
  
  const administratorLeadsRef = collection(db, ADMINISTRATOR_LEADS_COLLECTION);
  let q = query(administratorLeadsRef, orderBy('created_at', 'desc'));
  
  if (pageSize) {
    q = query(q, limit(pageSize));
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
  }
  
  const snapshot = await getDocs(q);
  console.log('[API] getAdministratorLeads result:', { docsCount: snapshot.docs.length, hasNext: snapshot.docs.length === pageSize });
  const administratorLeads = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      created_at: data.created_at?.toDate(),
      updated_at: data.updated_at?.toDate(),
      activities: data.activities?.map((activity: any) => ({
        ...activity,
        date: activity.date?.toDate(),
        created_at: activity.created_at?.toDate(),
      })) || [],
      opportunity: data.opportunity ? {
        ...data.opportunity,
        expected_close_date: data.opportunity.expected_close_date?.toDate(),
      } : undefined,
    };
  }) as AdministratorLead[];
  
  const newLastDoc = snapshot.docs[snapshot.docs.length - 1];
  return {
    administratorLeads,
    lastDoc: newLastDoc
  };
}

export async function getAdministratorLeadById(id: string): Promise<AdministratorLead | null> {
  const ref = doc(db, ADMINISTRATOR_LEADS_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  
  const data = snap.data();
  return {
    id: snap.id,
    ...data,
    created_at: data.created_at?.toDate(),
    updated_at: data.updated_at?.toDate(),
    activities: data.activities?.map((activity: any) => ({
      ...activity,
      date: activity.date?.toDate(),
      created_at: activity.created_at?.toDate(),
    })) || [],
    opportunity: data.opportunity ? {
      ...data.opportunity,
      expected_close_date: data.opportunity.expected_close_date?.toDate(),
    } : undefined,
  } as AdministratorLead;
}

export async function addAdministratorLead(administratorLead: Omit<AdministratorLead, 'id'>): Promise<string> {
  const ref = collection(db, ADMINISTRATOR_LEADS_COLLECTION);
  
  // Generate a unique unsubscribe token
  const unsubscribeToken = `unsub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const docRef = await addDoc(ref, {
    ...administratorLead,
    unsubscribe_token: unsubscribeToken,
    created_at: new Date(),
    updated_at: new Date(),
  });
  return docRef.id;
}

export async function updateAdministratorLead(id: string, administratorLead: Partial<AdministratorLead>): Promise<void> {
  const ref = doc(db, ADMINISTRATOR_LEADS_COLLECTION, id);
  await updateDoc(ref, {
    ...administratorLead,
    updated_at: new Date(),
  });
}

export async function deleteAdministratorLead(id: string): Promise<void> {
  const ref = doc(db, ADMINISTRATOR_LEADS_COLLECTION, id);
  await deleteDoc(ref);
}

export async function addOrUpdateAdministratorOpportunity(
  administratorLeadId: string,
  opportunity: AdministratorLead['opportunity']
): Promise<void> {
  const ref = doc(db, ADMINISTRATOR_LEADS_COLLECTION, administratorLeadId);
  await updateDoc(ref, {
    opportunity,
    updated_at: new Date(),
  });
}

export async function updateAdministratorLeadStatus(leadId: string, status: string): Promise<void> {
  const leadRef = doc(db, ADMINISTRATOR_LEADS_COLLECTION, leadId);
  
  await updateDoc(leadRef, {
    status,
    updated_at: new Date(),
  });
}

export async function toggleAdministratorLeadWhatsApp(leadId: string, whatsappUnavailable: boolean): Promise<void> {
  const leadRef = doc(db, ADMINISTRATOR_LEADS_COLLECTION, leadId);
  
  await updateDoc(leadRef, {
    whatsapp_unavailable: whatsappUnavailable,
    updated_at: new Date(),
  });
}

export async function unsubscribeFromEmails(token: string, email?: string): Promise<{ success: boolean; error?: string; leadName?: string }> {
  try {
    // Find lead by token (could be unsubscribe_token or lead ID)
    const leadsRef = collection(db, ADMINISTRATOR_LEADS_COLLECTION);
    const q = query(
      leadsRef,
      where('unsubscribe_token', '==', token)
    );
    
    let querySnapshot = await getDocs(q);
    
    // If no lead found with unsubscribe_token, try with lead ID
    if (querySnapshot.empty) {
      const leadRef = doc(db, ADMINISTRATOR_LEADS_COLLECTION, token);
      const leadDoc = await getDoc(leadRef);
      
      if (leadDoc.exists()) {
        const leadData = leadDoc.data();
        
        // Enhanced security: If email is provided, verify it matches the lead's email
        if (email) {
          const leadEmails = leadData.contacts?.map((contact: any) => contact.email).filter(Boolean) || [];
          if (!leadEmails.includes(email)) {
            return {
              success: false,
              error: 'El correo electrónico no coincide con el registro'
            };
          }
        }
        
        await updateDoc(leadRef, {
          status: 'Lost',
          updated_at: new Date(),
        });
        
        return {
          success: true,
          leadName: leadData.name
        };
      }
    } else {
      // Lead found with unsubscribe_token
      const leadDoc = querySnapshot.docs[0];
      const leadData = leadDoc.data();
      
      // Enhanced security: If email is provided, verify it matches the lead's email
      if (email) {
        const leadEmails = leadData.contacts?.map((contact: any) => contact.email).filter(Boolean) || [];
        if (!leadEmails.includes(email)) {
          return {
            success: false,
            error: 'El correo electrónico no coincide con el registro'
          };
        }
      }
      
      await updateDoc(leadDoc.ref, {
        status: 'Lost',
        updated_at: new Date(),
      });
      
      return {
        success: true,
        leadName: leadData.name
      };
    }
    
    return {
      success: false,
      error: 'Token de desuscripción no válido'
    };
  } catch (error) {
    console.error('Error unsubscribing:', error);
    return {
      success: false,
      error: 'Error interno del servidor'
    };
  }
}

// Function to import MINVU data with batch processing
export async function importMinvuData(minvuData: any[]): Promise<{ success: number; errors: string[] }> {
  // Use the batch import function for actual Firebase saving
  return await importMinvuDataBatch(minvuData);
}

// Optimized batch import function (when Firebase is enabled)
export async function importMinvuDataBatch(minvuData: any[]): Promise<{ success: number; errors: string[] }> {
  const errors: string[] = [];
  let success = 0;

  console.log(`📊 Starting batch import of ${minvuData.length} MINVU records...`);

  // Process in batches of 500 (Firestore batch limit)
  const BATCH_SIZE = 500;
  const batches = [];
  
  for (let i = 0; i < minvuData.length; i += BATCH_SIZE) {
    batches.push(minvuData.slice(i, i + BATCH_SIZE));
  }

  console.log(`📦 Processing ${batches.length} batches of up to ${BATCH_SIZE} records each`);

  for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
    const batch = batches[batchIndex];
    console.log(`🔄 Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} records)`);

    try {
      // Create Firestore batch
      const firestoreBatch = writeBatch(db);
      const administratorLeadsRef = collection(db, ADMINISTRATOR_LEADS_COLLECTION);

      for (const record of batch) {
        try {
          // Map MINVU data to AdministratorLead structure
          const administratorLead: Omit<AdministratorLead, 'id'> = {
            // Handle missing name - only use nombre_completo
            name: record.nombre_completo || 'Administrador Desconocido',
            
            status: 'New' as const,
            source: 'MINVU Condominios' as LeadSource,
            
            // Handle missing contact data
            contacts: [
              {
                name: record.nombre_completo || 'Administrador Desconocido',
                email: record.email || '',
                phone: record.telefono_uno || '',
                position: 'Administrador',
                type: 'Primary' as const,
                value: record.email || record.telefono_uno || '',
              },
            ],
            
            // Handle missing address data
            address: [
              record.calle || 'Dirección no disponible',
              record.calle_numeracion || '',
              record.departamento ? `, Depto ${record.departamento}` : ''
            ].filter(Boolean).join(' ') || 'Dirección no disponible',
            
            // Handle missing city data
            city: record.id_comuna ? `Comuna ${record.id_comuna}` : 'Chile',
            
            // Handle missing pincode data
            pincode: record.id_region?.toString() || '0',
            
            country: 'Chile',
            
            // Handle missing description data
            description: [
              'Administrador de condominios registrado en MINVU.',
              record.estado ? `Estado: ${record.estado}` : 'Estado: No disponible',
              record.fecha_vigencia ? `Vigencia: ${record.fecha_vigencia}` : 'Vigencia: No disponible'
            ].filter(Boolean).join(' '),
            
            // Handle missing notes data with safe fallbacks
            notes: [
              record.rut ? `RUT: ${record.rut}` : 'RUT: No disponible',
              `ID Administrador: ${record.id_administrador}`,
              record.tipo ? `Tipo: ${record.tipo}` : 'Tipo: No disponible',
              record.clasificacion_inscripcion ? `Clasificación: ${record.clasificacion_inscripcion}` : 'Clasificación: No disponible',
              record.id_region ? `Región: ${record.id_region}` : 'Región: No disponible',
              record.id_comuna ? `Comuna: ${record.id_comuna}` : 'Comuna: No disponible',
              `Cursos: ${record.cursos?.length || 0} cursos completados`,
              record.certificado_antecedentes ? `Certificado: ${record.certificado_antecedentes}` : 'Certificado: No disponible'
            ].filter(Boolean).join('\n'),
            
            created_at: new Date(),
            updated_at: new Date(),
            activities: [],
          };

          // Add to batch
          const docRef = doc(administratorLeadsRef);
          firestoreBatch.set(docRef, administratorLead);
          success++;
        } catch (error) {
          console.error(`❌ Error mapping ${record.nombre_completo || record.id_administrador}:`, error);
          errors.push(`Error mapping ${record.nombre_completo || record.id_administrador}: ${error}`);
        }
      }

      // Commit batch to Firestore
      await firestoreBatch.commit();
      console.log(`✅ Batch ${batchIndex + 1} committed successfully`);

    } catch (error) {
      console.error(`❌ Error committing batch ${batchIndex + 1}:`, error);
      errors.push(`Error committing batch ${batchIndex + 1}: ${error}`);
    }
  }

  console.log(`🎉 Batch import complete: ${success} successful, ${errors.length} errors`);
  return { success, errors };
}

